import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  UserX, 
  Phone, 
  MessageCircle, 
  CheckCircle2, 
  CalendarPlus,
  Filter,
  RefreshCw,
  Clock,
  AlertCircle,
  XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPOINTMENT_TYPES = {
  primera_vez: { label: 'Primera Vez', color: 'bg-orange-100 text-orange-700' },
  control: { label: 'Control', color: 'bg-teal-100 text-teal-700' },
  emergencia: { label: 'Emergencia', color: 'bg-red-100 text-red-700' },
  cirugia: { label: 'Cirugía', color: 'bg-pink-100 text-pink-700' },
  consulta_rapida: { label: 'Consulta Rápida', color: 'bg-green-100 text-green-700' },
};

const NoShowPage = () => {
  const { token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [hideContacted, setHideContacted] = useState(false);
  const [stats, setStats] = useState({ total: 0, pending: 0, contacted: 0 });

  const fetchNoShowAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/appointments/no-show/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days: parseInt(days), hide_contacted: hideContacted }
      });
      
      const data = response.data;
      setAppointments(data);
      
      // Calculate stats
      const contacted = data.filter(a => a.contacted_at).length;
      setStats({
        total: data.length,
        pending: data.length - contacted,
        contacted: contacted
      });
    } catch (error) {
      console.error('Error fetching no-show appointments:', error);
      toast.error('Error al cargar la lista');
    } finally {
      setLoading(false);
    }
  }, [token, days, hideContacted]);

  useEffect(() => {
    fetchNoShowAppointments();
  }, [fetchNoShowAppointments]);

  const handleWhatsApp = (appointment) => {
    if (!appointment.patient_phone) {
      toast.error('El paciente no tiene teléfono registrado');
      return;
    }

    const patientName = appointment.patient_name?.split(' ')[0] || 'paciente';
    const appointmentDate = format(
      new Date(appointment.appointment_date + 'T12:00:00'), 
      "d 'de' MMMM", 
      { locale: es }
    );

    const message = `Hola ${patientName}, notamos que no pudo asistir a su cita del ${appointmentDate}. ¿Le gustaría agendar una nueva cita? Estamos a su disposición.\n\n- Dr. Cristian R. Acevedo, Oftalmólogo\n📍 2 Av 13-71 Zona 1, Guatemala\n📞 50503321`;

    const cleanPhone = appointment.patient_phone.replace(/[^0-9]/g, '');
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleMarkContacted = async (appointmentId) => {
    try {
      await axios.put(
        `${API}/appointments/no-show/${appointmentId}/mark-contacted`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Marcado como contactado');
      fetchNoShowAppointments();
    } catch (error) {
      toast.error('Error al marcar como contactado');
    }
  };

  const handleUnmarkContacted = async (appointmentId) => {
    try {
      await axios.put(
        `${API}/appointments/no-show/${appointmentId}/unmark-contacted`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Marca eliminada');
      fetchNoShowAppointments();
    } catch (error) {
      toast.error('Error al eliminar marca');
    }
  };

  const handleScheduleNew = (appointment) => {
    // Navigate to calendar with patient pre-selected
    window.location.href = `/admin/calendar?patient_id=${appointment.patient_id}&patient_name=${encodeURIComponent(appointment.patient_name || '')}`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display text-slate-900 flex items-center gap-2">
            <UserX className="h-7 w-7 text-red-500" />
            Pacientes No Asistieron
          </h1>
          <p className="text-slate-500 mt-1">
            Seguimiento de citas perdidas y re-agendamiento
          </p>
        </div>
        
        <Button 
          variant="outline" 
          onClick={fetchNoShowAppointments}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-full">
              <UserX className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total No Asistieron</p>
              <p className="text-2xl font-bold text-red-600">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-full">
              <AlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Pendientes Contactar</p>
              <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Ya Contactados</p>
              <p className="text-2xl font-bold text-green-600">{stats.contacted}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Filtros:</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-500">Período:</Label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Últimos 7 días</SelectItem>
                  <SelectItem value="15">Últimos 15 días</SelectItem>
                  <SelectItem value="30">Últimos 30 días</SelectItem>
                  <SelectItem value="60">Últimos 60 días</SelectItem>
                  <SelectItem value="90">Últimos 90 días</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox 
                id="hideContacted" 
                checked={hideContacted}
                onCheckedChange={setHideContacted}
              />
              <Label htmlFor="hideContacted" className="text-sm cursor-pointer">
                Ocultar ya contactados
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Appointments List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-400" />
            Lista de Citas No Asistidas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">
              Cargando...
            </div>
          ) : appointments.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="h-12 w-12 text-green-300 mx-auto mb-4" />
              <p className="text-slate-500">No hay citas sin asistir en este período</p>
              <p className="text-sm text-slate-400">¡Excelente! Todos los pacientes asistieron.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {appointments.map((apt) => {
                const typeConfig = APPOINTMENT_TYPES[apt.appointment_type] || { 
                  label: apt.appointment_type, 
                  color: 'bg-slate-100 text-slate-700' 
                };
                const isContacted = !!apt.contacted_at;
                
                return (
                  <div 
                    key={apt.id}
                    className={`border rounded-lg p-4 transition-all ${
                      isContacted 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-white hover:bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      {/* Patient Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-slate-900">
                            {apt.patient_name || 'Paciente sin nombre'}
                          </h3>
                          <Badge className={typeConfig.color}>
                            {typeConfig.label}
                          </Badge>
                          {isContacted && (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Contactado
                            </Badge>
                          )}
                        </div>
                        
                        <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {format(new Date(apt.appointment_date + 'T12:00:00'), "EEEE d 'de' MMMM, yyyy", { locale: es })}
                            {apt.appointment_time && ` - ${apt.appointment_time}`}
                          </span>
                          {apt.patient_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-4 w-4" />
                              {apt.patient_phone}
                            </span>
                          )}
                        </div>
                        
                        {isContacted && apt.contacted_at && (
                          <p className="mt-2 text-xs text-green-600">
                            Contactado el {format(new Date(apt.contacted_at), "d/MM/yyyy 'a las' HH:mm", { locale: es })}
                            {apt.contacted_by && ` por ${apt.contacted_by}`}
                          </p>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {apt.patient_phone && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() => handleWhatsApp(apt)}
                            title="Enviar WhatsApp"
                          >
                            <MessageCircle className="h-4 w-4 mr-1" />
                            WhatsApp
                          </Button>
                        )}
                        
                        {!isContacted ? (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => handleMarkContacted(apt.id)}
                            title="Marcar como contactado"
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Contactado
                          </Button>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-slate-400 hover:text-red-500"
                            onClick={() => handleUnmarkContacted(apt.id)}
                            title="Desmarcar contactado"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}
                        
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-[#0B3B71] border-[#0B3B71]/20 hover:bg-[#0B3B71]/5"
                          onClick={() => handleScheduleNew(apt)}
                          title="Agendar nueva cita"
                        >
                          <CalendarPlus className="h-4 w-4 mr-1" />
                          Nueva Cita
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NoShowPage;
